import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, Stethoscope, Building2, FlaskConical, X } from "lucide-react";
import api from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * Global header search — debounced, keyboard-navigable dropdown.
 * Queries /api/search for doctors, hospitals, and CRO trials.
 */
export default function GlobalSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  // Debounce search
  useEffect(() => {
    if (query.trim().length < 2) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const res = await api.get(`/search?q=${encodeURIComponent(query.trim())}&limit=5`);
        setResults(res.data);
        setHighlight(0);
      } catch {
        setResults({ doctors: [], hospitals: [], trials: [], total: 0 });
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(handle);
  }, [query]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Flatten results for keyboard nav
  const flat = results
    ? [
        ...results.doctors.map((d) => ({ ...d, type: "doctor", icon: Stethoscope })),
        ...results.hospitals.map((h) => ({ ...h, type: "hospital", icon: Building2 })),
        ...results.trials.map((t) => ({ ...t, type: "trial", icon: FlaskConical })),
      ]
    : [];

  const go = (item) => {
    if (!item) return;
    navigate(item.route);
    setOpen(false);
    setQuery("");
    setResults(null);
  };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, flat.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (flat[highlight]) go(flat[highlight]); }
    else if (e.key === "Escape") { setOpen(false); inputRef.current?.blur(); }
    else if (e.key === "/" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); inputRef.current?.focus(); }
  };

  return (
    <div ref={containerRef} className="relative w-64 xl:w-80" data-testid="global-search">
      <div className={cn(
        "flex items-center gap-2 px-3 py-1.5 rounded-full border transition-colors",
        open ? "border-primary bg-white" : "border-border bg-muted/40 hover:bg-white"
      )}>
        <Search size={13} className="text-muted-foreground shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder="Search doctors, hospitals, trials…"
          data-testid="global-search-input"
          className="flex-1 bg-transparent outline-none text-xs placeholder:text-muted-foreground/70 min-w-0"
        />
        {loading && <Loader2 size={12} className="text-muted-foreground animate-spin shrink-0" />}
        {query && !loading && (
          <button
            type="button"
            data-testid="global-search-clear"
            onClick={() => { setQuery(""); setResults(null); inputRef.current?.focus(); }}
            className="text-muted-foreground hover:text-destructive shrink-0"
          >
            <X size={12} />
          </button>
        )}
      </div>

      {open && query.trim().length >= 2 && (
        <div
          className="absolute left-0 right-0 top-full mt-2 rounded-lg border border-border bg-white shadow-xl z-50 overflow-hidden"
          data-testid="global-search-dropdown"
        >
          {results === null && (
            <div className="px-4 py-6 text-center text-xs text-muted-foreground">Searching…</div>
          )}
          {results && results.total === 0 && (
            <div className="px-4 py-6 text-center text-xs text-muted-foreground" data-testid="global-search-empty">
              No matches for &quot;<span className="text-primary">{query}</span>&quot;
            </div>
          )}
          {results && results.total > 0 && (
            <ul className="max-h-96 overflow-y-auto py-1" data-testid="global-search-results">
              {flat.map((item, i) => {
                const Icon = item.icon;
                const isActive = i === highlight;
                return (
                  <li key={`${item.type}-${item.id}`}>
                    <button
                      type="button"
                      data-testid={`global-search-item-${item.type}-${item.id}`}
                      onMouseEnter={() => setHighlight(i)}
                      onClick={() => go(item)}
                      className={cn(
                        "w-full text-left px-3 py-2 flex items-center gap-3 transition-colors",
                        isActive ? "bg-pastel-mint-bg text-pastel-mint-fg" : "hover:bg-muted/60"
                      )}
                    >
                      <span className={cn(
                        "shrink-0 w-6 h-6 rounded-md flex items-center justify-center",
                        item.type === "doctor" ? "bg-pastel-sky-bg text-pastel-sky-fg"
                        : item.type === "hospital" ? "bg-pastel-lavender-bg text-pastel-lavender-fg"
                        : "bg-pastel-peach-bg text-pastel-peach-fg"
                      )}>
                        <Icon size={12} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium text-primary truncate">{item.name}</div>
                        <div className="text-[10px] text-muted-foreground truncate">
                          {item.type === "doctor"   && `${item.specialty} · ${item.city}`}
                          {item.type === "hospital" && `${item.city}`}
                          {item.type === "trial"    && `${item.phase} · ${item.therapeutic_area} · ${item.sponsor}`}
                        </div>
                      </div>
                      <span className="text-[9px] uppercase tracking-wider text-muted-foreground shrink-0">
                        {item.type}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
